'use client';

import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { BaseHttpResponseError, type ErrorCodeType } from '../utils/error/apiErrors';
export type QueryErrorHandler = (errorCode: ErrorCodeType | undefined, error: BaseHttpResponseError) => void;
let _errorHandler: QueryErrorHandler | undefined;
function setQueryErrorHandler(handler: QueryErrorHandler | undefined) {
  _errorHandler = handler;
}
export function useRegisterQueryErrorHandler(handler: QueryErrorHandler) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  useEffect(() => {
    setQueryErrorHandler((errorCode, error) => handlerRef.current(errorCode, error));
    return () => setQueryErrorHandler(undefined);
  }, []);
}
export const createQueryClient = () => {
  const queryCache = new QueryCache({
    onError(error, query) {
      console.error(`Error in query ${String(query.queryKey)}:`, error);
      if (error instanceof BaseHttpResponseError) {
        _errorHandler?.(error.serverResponse.errorData?.code, error);
      }
    }
  });
  const mutationCache = new MutationCache({
    onError(error, _variables, _context, _mutation) {
      if (error instanceof BaseHttpResponseError) {
        _errorHandler?.(error.serverResponse.errorData?.code, error);
      }
    }
  });
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: 0
      }
    },
    queryCache,
    mutationCache
  });
};
export const queryClient = createQueryClient();